<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import axios from 'axios';
import { nextTick } from 'vue'; // Import nextTick for notification
import { API_BASE_URL } from '@/config';

// !! IMPORTANT: Replace with the actual base URL of your Node.js backend !!
const API_SITES_URL = '${API_BASE_URL}/api/sites'; // API endpoint for sites

// Reactive state for the list of sites
const sites = ref([]);
// Reactive state for the lists to populate dropdowns
const supervisors = ref([]); // To store employee data for supervision dropdown
const plants = ref([]); // To store plant data for plant dropdown


// Reactive state for the new site form input - Removed Tax1, Tax2, Guide
const newSite = ref({
    SiteCode: '',
    Sitename: '',
    sitesupervisionid: null, // Use null for nullable fields
    SiteFax: null, // Use null for nullable fields
    PlantNo: '', // Assuming required based on previous code
    // Removed: Tax1, Tax2, guide
});

// Reactive state to hold the site currently being edited (null if not editing)
const editingSite = ref(null);

// Reactive state to track the row clicked in the table for Edit/Delete actions
const selectedSite = ref(null);

const loading = ref(false); // General loading state for async operations
const loadingSupervisors = ref(false); // Specific loading indicator for fetching supervisors
const loadingPlants = ref(false); // Specific loading indicator for fetching plants
const loadingSites = ref(false); // Specific loading indicator for fetching site list


const siteListError = ref(null); // Error state for displaying persistent error messages for the list
const formError = ref(null); // Error state for displaying persistent error messages for the form

// State for managing temporary notification messages (success, error, warning)
const notification = ref({
    show: false,
    message: '',
    type: '' // 'success', 'error', 'warning'
});


// --- Computed Properties for v-model Binding ---
// This computed property handles the binding for the form inputs.
// It switches between the 'editingSite' and 'newSite' objects based on whether editing is active.
const currentSite = computed({
    get() {
        return editingSite.value || newSite.value;
    },
    set(value) {
        if (editingSite.value) {
            // Use Object.assign to update the properties of the reactive object
            Object.assign(editingSite.value, value);
        } else {
            // Use Object.assign to update the properties of the reactive object
            Object.assign(newSite.value, value);
        }
    }
});

// Simple form validation check - Requires SiteCode, Sitename, and PlantNo
const isFormValid = computed(() => {
    return currentSite.value.SiteCode.trim() && currentSite.value.Sitename.trim() && currentSite.value.PlantNo;
});

// --- Data Fetching for Dropdowns and Table ---

// Fetches all sites for the table
const fetchSites = async () => {
    loadingSites.value = true; // Set specific loading state
    siteListError.value = null; // Clear any previous errors
    selectedSite.value = null; // Deselect any selected item when refreshing the list
    sites.value = []; // Clear existing data while loading
    try {
        const response = await axios.get(API_SITES_URL);
        sites.value = response.data;
    } catch (err) {
        console.error('Error fetching sites:', err);
        siteListError.value = 'Failed to fetch sites. ' + (err.response?.data || err.message);
        showNotification('Failed to fetch sites.', 'error'); // Show an error notification
    } finally {
        loadingSites.value = false; // Set loading state to false regardless of success or failure
    }
};

// Fetches employees for the supervision dropdown
const fetchSupervisors = async () => {
    loadingSupervisors.value = true; // Set specific loading state
    try {
         // Assuming the endpoint is now relative to the sites API path
        const response = await axios.get(`${API_SITES_URL}/supervision-employees`);
        // Assuming the response data is an array of objects like { emp_no: '...', emp_name: '...' }
        supervisors.value = response.data;
    } catch (err) {
        console.error('Error fetching supervisors:', err);
         showNotification('Failed to fetch supervisors for dropdown.', 'error');
    } finally {
         loadingSupervisors.value = false; // Set specific loading state
    }
};

// Fetches plants for the plant dropdown
const fetchPlants = async () => {
    loadingPlants.value = true; // Set specific loading state
    try {
        // Assuming the endpoint is now relative to the sites API path
        const response = await axios.get(`${API_SITES_URL}/plants`); // Assuming '/api/sites/plants' is the endpoint
         // Assuming the response data is an array of objects like { PlantNo: '...', PlantName: '...' }
        plants.value = response.data;
    } catch (err) {
        console.error('Error fetching plants:', err);
         showNotification('Failed to fetch plants for dropdown.', 'error');
    } finally {
         loadingPlants.value = false; // Set specific loading state
    }
};


// --- CRUD Operations ---

// Handles saving (creating or updating) a site
const saveSite = async () => {
    // Basic client-side validation
    if (!isFormValid.value) {
        formError.value = 'Site Code, Site Name, and Plant are required.';
        showNotification(formError.value, 'warning');
        return; // Stop the function if validation fails
    }

    loading.value = true; // Set general loading state
    formError.value = null; // Clear previous errors

    // Construct the payload with form fields (Excluding Tax1, Tax2, Guide)
    const payload = {
        SiteCode: currentSite.value.SiteCode,
        Sitename: currentSite.value.Sitename,
        sitesupervisionid: currentSite.value.sitesupervisionid || null, // Send null for empty optional fields
        SiteFax: currentSite.value.SiteFax || null,
        PlantNo: currentSite.value.PlantNo, // Assuming PlantNo is required
        // Removed: Tax1, Tax2, guide from payload
    };

    try {
        if (editingSite.value) {
            // Update existing site
            const response = await axios.put(`${API_SITES_URL}/${editingSite.value.SiteCode}`, payload);
            showNotification('Site updated successfully!', 'success');
            // Find and replace the updated site in the local sites array
            const index = sites.value.findIndex(site => site.SiteCode === editingSite.value.SiteCode);
            if (index !== -1) {
                // Update the existing object properties to keep reactivity
                 Object.assign(sites.value[index], response.data);
            }
        } else {
            // Create new site
            const response = await axios.post(API_SITES_URL, payload);
            showNotification('Site created successfully!', 'success');
            // Add the new site to the local sites array
            sites.value.push(response.data);
        }
        cancelEditing(); // Reset form and state after successful save/update
    } catch (err) {
        console.error('Error saving site:', err);
         const errorMessage = err.response?.data || err.message || 'An unexpected error occurred.';
         formError.value = 'Failed to save site: ' + errorMessage; // Display error above the form
         showNotification('Failed to save site.', 'error');
    } finally {
        loading.value = false; // Set general loading state
    }
};


// Deletes a site by its unique SiteCode
const deleteSite = async () => {
    // Ensure a site is selected for deletion
    if (!selectedSite.value) {
        showNotification('Please select a site to delete.', 'warning');
        return;
    }
    // Get the SiteCode of the item to delete from the selected item
    const siteCodeToDelete = selectedSite.value.SiteCode;

    if (confirm(`Are you sure you want to delete site ${siteCodeToDelete}? This action cannot be undone and may affect related records.`)) {
        loading.value = true; // Set general loading state
        formError.value = null; // Clear form errors
        try {
            // Send DELETE request to the specific item endpoint using the SiteCode
            await axios.delete(`${API_SITES_URL}/${siteCodeToDelete}`);
            // Remove the deleted item from the local list by filtering
            sites.value = sites.value.filter(item => item.SiteCode !== siteCodeToDelete);
            // Clear selected and editing states if they match the deleted item
            selectedSite.value = null;
            if(editingSite.value && editingSite.value.SiteCode === siteCodeToDelete) {
                 editingSite.value = null;
            }
             resetForm(); // Clear the form after deletion
            showNotification('Site deleted successfully!', 'success');
        } catch (err) {
            console.error('Error deleting site:', err);
            // Handle specific error responses from the backend
            if (err.response?.status === 404) {
                 formError.value = 'Site not found or already deleted.';
            } else if (err.response?.status === 409) {
                 formError.value = err.response.data || 'Conflict: Cannot delete site because it is referenced by other entities.';
            }
            else {
                formError.value = err.response?.data || 'Failed to delete site.';
            }
            showNotification(formError.value, 'error');
        } finally {
            loading.value = false; // Set general loading state
        }
    }
};

// --- UI Actions ---

// Selects a row in the table when clicked
const selectSite = (item) => {
    // Prevent selection change if currently editing
    if (!editingSite.value) {
      selectedSite.value = item;
      formError.value = null; // Clear previous form errors when selecting a new row
    } else {
        // Optionally show a message that user is in editing mode
         showNotification('Finish editing or cancel before selecting another site.', 'warning');
    }
}

// Initiates the editing process for the currently selected site
const startEditing = () => {
    if (selectedSite.value) {
        // Copy selected item data to the editing state to populate the form
        editingSite.value = { ...selectedSite.value };
        resetNewSiteForm(); // Clear the new site form when starting edit mode
        formError.value = null; // Clear any previous form errors
    } else {
        showNotification('Please select a site to edit.', 'warning'); // Prompt user if no item is selected
    }
};

// Cancels the current editing process and clears the editing state
const cancelEditing = () => {
    editingSite.value = null; // Clear the editing state
    resetForm(); // Reset the form (clears inputs whether in new or edit mode)
    selectedSite.value = null; // Deselect after cancelling edit
    formError.value = null; // Clear any previous form errors
};

// Resets the form fields for adding a new site
const addNew = () => {
    editingSite.value = null; // Ensure not in editing mode
    selectedSite.value = null; // Deselect any row in the table
    resetNewSiteForm(); // Clear form for new entry
    formError.value = null; // Clear any previous form errors
};

// Helper to reset the new site form - Removed Tax1, Tax2, Guide
const resetNewSiteForm = () => {
    newSite.value = {
        SiteCode: '',
        Sitename: '',
        sitesupervisionid: null,
        SiteFax: null,
        PlantNo: '',
        // Removed: Tax1, Tax2, guide
    };
};

// Helper to reset the entire form state (used after saving/cancelling)
const resetForm = () => {
    resetNewSiteForm();
     // If editingSite was active, reset its fields too if needed,
     // but currentSite handles the binding, so resetting newSite usually suffices.
     // For a more explicit reset of the editing state:
     if (editingSite.value) {
          editingSite.value = { // Reset editing fields to default or blank (Removed Tax1, Tax2, Guide)
             SiteCode: '',
             Sitename: '',
             sitesupervisionid: null,
             SiteFax: null,
             PlantNo: '',
         };
     }
};


// Helper function to show a temporary notification message
const showNotification = (message, type) => {
    notification.value.message = message;
    notification.value.type = type;
    notification.value.show = true;

    nextTick(() => {
        const notificationElement = document.querySelector('.notification');
        if (notificationElement) {
             // Remove any existing fade-out class to restart the animation
             notificationElement.classList.remove('fade-out');
             // Trigger a reflow to ensure the animation restarts if triggered quickly
             void notificationElement.offsetWidth;

            setTimeout(() => {
                notificationElement.classList.add('fade-out');
                // Use the transitionend event to hide the element completely after the animation
                notificationElement.addEventListener('transitionend', () => {
                     // Check if the fade-out class is still present before hiding (prevents issues if triggered multiple times)
                     if (!notificationElement.classList.contains('fade-out')) return;
                     notification.value.show = false;
                }, { once: true }); // Ensure the event listener is removed after it's used once
            }, 5000); // Start fade-out after 5 seconds
        } else {
             // Fallback if the notification element is not found immediately (less ideal)
             setTimeout(() => {
                 notification.value.show = false;
             }, 5500);
        }
    });
};

// --- Lifecycle Hook ---
// Fetch data when the component is first mounted to the DOM
onMounted(() => {
    fetchSites();
    fetchSupervisors(); // Fetch supervisors for dropdown
    fetchPlants(); // Fetch plants for dropdown
});
</script>

<template>
    <div class="sites-page">
        <div class="container">
            <div class="form-panel">
                <h2>{{ editingSite ? 'Edit Site' : 'Add New Site' }}</h2>
                <form @submit.prevent="saveSite"> <div class="form-group">
                        <label for="site_code">Site Code:</label>
                        <input type="text" id="site_code" v-model="currentSite.SiteCode" required
                               :disabled="editingSite !== null || loading"> </div>
                    <div class="form-group">
                        <label for="site_name">Site Name:</label>
                        <input type="text" id="site_name" v-model="currentSite.Sitename" required
                               :disabled="loading">
                    </div>

                    <div class="form-group">
                        <label for="supervision_id">Supervision:</label>
                        <select id="supervision_id" v-model="currentSite.sitesupervisionid" :disabled="loading || loadingSupervisors">
                            <option :value="null" disabled>Select Supervisor</option> <option v-for="employee in supervisors" :key="employee.emp_no" :value="employee.emp_no">
                                {{ employee.emp_name }} ({{ employee.emp_no }})
                            </option>
                            <option :value="null" v-if="supervisors.length === 0 && !loadingSupervisors">No supervisors available</option>
                            <option :value="null" v-if="loadingSupervisors">Loading supervisors...</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label for="fax">Fax:</label>
                        <input type="text" id="fax" v-model="currentSite.SiteFax" :disabled="loading">
                    </div>
                    <div class="form-group">
                        <label for="plant_no">Plant:</label>
                        <select id="plant_no" v-model="currentSite.PlantNo" required :disabled="loading || loadingPlants">
                            <option value="" disabled>Select Plant</option> <option v-for="plant in plants" :key="plant.PlantNo" :value="plant.PlantNo">
                                {{ plant.PlantName }} ({{ plant.PlantNo }})
                            </option>
                            <option value="" v-if="plants.length === 0 && !loadingPlants">No plants available</option>
                             <option value="" v-if="loadingPlants">Loading plants...</option>
                        </select>
                    </div>
                    <div class="form-actions">
                        <button type="submit" class="save-button" :disabled="loading || loadingSupervisors || loadingPlants || !isFormValid">
                            {{ editingSite ? 'Update Site' : 'Save Site' }}
                        </button>
                        <button type="button" class="cancel-button" @click="cancelEditing" v-if="editingSite" :disabled="loading">
                            Cancel
                        </button>
                    </div>
                </form>
                 <div v-if="formError" class="error-message">{{ formError }}</div>
            </div>

            <div class="table-panel">
                <h2>Site List</h2>

                <div v-if="loadingSites">Loading sites...</div>
                <div v-if="siteListError && sites.length === 0" class="error-message">{{ siteListError }}</div>


                <div class="table-actions">
                    <button @click="addNew" :disabled="loading || loadingSites">Add New</button>
                    <button @click="startEditing" :disabled="!selectedSite || editingSite || loading || loadingSites">Edit</button>
                    <button @click="deleteSite" :disabled="!selectedSite || loading || loadingSites">Delete</button>
                    <button @click="fetchSites" :disabled="loading || loadingSites">Refresh</button>
                </div>

                <table class="sites-table"> <thead>
                        <tr>
                            <th>Site Code</th>
                            <th>Site Name</th>
                            </tr>
                    </thead>
                    <tbody>
                        <tr v-for="item in sites" :key="item.SiteCode"
                            :class="{ 'selected': selectedSite && selectedSite.SiteCode === item.SiteCode }"
                            @click="selectSite(item)">
                            <td>{{ item.SiteCode }}</td>
                            <td>{{ item.Sitename }}</td>
                            </tr>
                         <tr v-if="sites.length === 0 && !loadingSites && !siteListError">
                             <td colspan="2" style="text-align: center;">No sites found.</td> </tr>
                    </tbody>
                </table>
            </div>
        </div>

         <div v-if="notification.show" :class="['notification', notification.type, { 'fade-out': !notification.show }]">
            {{ notification.message }}.
        </div>

    </div>
</template>

<style scoped>
/* Styles adopted from the EmployeeView example for consistency and professional look */
.sites-page { /* Unique class name for this page */
    padding: 20px;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    background-color: #f4f6f8;
    min-height: calc(100vh - 40px); /* Ensure page takes at least full height */
    color: #333; /* Default text color */
}

.container {
    display: flex;
    gap: 20px;
    max-width: 1200px; /* Adjusted max width to accommodate form and table */
    margin: 0 auto;
    flex-wrap: wrap;
    align-items: flex-start; /* Align items to the top */
}

.form-panel, .table-panel {
    background-color: white;
    padding: 25px;
    border-radius: 8px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    min-width: 300px;
     box-sizing: border-box; /* Include padding and border in the element's total width and height */
}

.form-panel {
     flex: 1 1 400px; /* Flex-grow, Flex-shrink, Basis - Adjusted basis for more fields */
     max-width: 500px; /* Optional: Limit max width for form */
}

.table-panel {
     flex: 2 1 500px; /* Adjusted flex basis */
    overflow-x: auto;
}


h2 {
    color: #333;
    margin-top: 0;
    margin-bottom: 25px;
    border-bottom: 1px solid #eee;
    padding-bottom: 15px;
    font-size: 1.5rem;
    font-weight: 600; /* Slightly bolder heading */
}

.form-group {
    margin-bottom: 20px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.form-group label {
    display: block;
    color: #555;
    font-weight: bold;
    font-size: 0.95rem;
}

input[type="text"],
input[type="number"],
select,
textarea { /* Added textarea styling */
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #ccc;
    border-radius: 4px;
    box-sizing: border-box;
    font-size: 1rem;
    transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

input[type="text"]:focus,
input[type="number"]:focus,
select:focus,
textarea:focus { /* Added textarea focus styling */
    border-color: #007bff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    outline: none;
}

input[type="text"]:disabled,
input[type="number"]:disabled,
select:disabled,
textarea:disabled { /* Added textarea disabled styling */
    background-color: #e9ecef;
    cursor: not-allowed;
    opacity: 0.8;
}

textarea {
    resize: vertical; /* Allow vertical resizing */
    min-height: 80px; /* Minimum height for textarea */
}


.form-actions {
    margin-top: 30px;
    display: flex;
    gap: 10px;
     flex-wrap: wrap; /* Allow buttons to wrap on small screens */
}

.save-button,
.cancel-button {
    padding: 10px 20px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 1rem;
    transition: background-color 0.3s ease, opacity 0.3s ease;
     min-width: 90px; /* Ensure minimum button width */
     text-align: center;
     flex-grow: 1; /* Allow buttons to grow if needed */
}

.save-button {
    background-color: #28a745; /* Green */
    color: white;
}

.save-button:hover:not(:disabled) {
    background-color: #218838;
}

.cancel-button {
    background-color: #6c757d; /* Gray */
    color: white;
}

.cancel-button:hover:not(:disabled) {
    background-color: #5a6268;
}

button:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
    opacity: 0.6;
}


.table-actions {
    margin-bottom: 15px;
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
}

.table-actions button {
    padding: 8px 12px;
    border: 1px solid #007bff;
    border-radius: 4px;
    background-color: #007bff;
    color: white;
    cursor: pointer;
    transition: background-color 0.3s ease, opacity 0.3s ease;
     flex-grow: 1;
     min-width: 60px;
     text-align: center;
}

/* Specific button styles */
.table-actions button:nth-child(2) { /* Edit button */
    background-color: #ffc107;
    border-color: #ffc107;
    color: #212529;
}
.table-actions button:nth-child(2):hover:not(:disabled) {
    background-color: #e0a800;
}

.table-actions button:nth-child(3) { /* Delete button */
    background-color: #dc3545;
    border-color: #dc3545;
    color: white;
}
.table-actions button:nth-child(3):hover:not(:disabled) {
    background-color: #c82333;
}

.table-actions button:hover:not(:disabled) {
    background-color: #0056b3;
    border-color: #0056b3;
}


.sites-table { /* Renamed table class */
    width: 100%;
    border-collapse: collapse;
    margin-top: 10px;
    font-size: 0.95rem;
    border: 1px solid #ddd;
}

.sites-table th,
.sites-table td {
    padding: 12px;
    border: 1px solid #ddd;
    text-align: left;
    word-break: break-word;
}

.sites-table th {
    background-color: #e9ecef;
    font-weight: bold;
    color: #495057;
    text-transform: uppercase;
    font-size: 0.85rem;
}

 .sites-table tbody tr:nth-child(even) {
     background-color: #f8f9fa;
 }

.sites-table tbody tr:hover {
    background-color: #e9ecef;
    cursor: pointer;
}

.sites-table tbody tr.selected {
    background-color: #cfe2ff;
     outline: 2px solid #007bff;
     outline-offset: -2px;
}

 /* Center text in empty cell */
 .sites-table td[colspan="2"] { /* Updated colspan */
     text-align: center;
     font-style: italic;
     color: #666;
 }


/* Responsive adjustments */
@media (max-width: 768px) {
    .container {
        flex-direction: column;
        gap: 15px;
    }

    .form-panel,
    .table-panel {
        margin-bottom: 15px;
        min-width: unset;
        flex: unset;
        width: 100%;
        padding: 15px;
    }

    h2 {
        margin-bottom: 15px;
        padding-bottom: 10px;
        font-size: 1.3rem;
    }

    .form-group {
        margin-bottom: 15px;
    }

    .form-actions {
        margin-top: 20px;
        flex-direction: column;
        gap: 8px;
    }

    .save-button, .cancel-button {
        width: 100%;
        padding: 10px;
         min-width: unset;
    }

    .table-actions {
        flex-direction: column;
        gap: 8px;
    }

    .table-actions button {
        width: 100%;
        padding: 10px;
         min-width: unset;
    }
}

/* Notification Styling */
.notification {
    position: fixed;
    bottom: 20px;
    left: 20px; /* Adjusted position to left */
    padding: 15px 20px;
    border-radius: 4px;
    color: white;
    z-index: 1000;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
     opacity: 1;
     transition: opacity 0.5s ease-in-out;
}

 .notification.fade-out {
     opacity: 0;
 }


.notification.success {
    background-color: #28a745;
}

.notification.error {
    background-color: #dc3545;
}

.notification.warning {
    background-color: #ffc107;
    color: #212529;
}

.error-message {
    color: #dc3545;
    margin-bottom: 15px;
    margin-top: 10px; /* Added margin top for spacing below form */
    padding: 10px;
    border: 1px solid #dc3545;
    background-color: #fff3f3;
    border-radius: 4px;
     font-size: 0.9rem;
}

</style>